// src/components/NotFound.jsx
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HomeIcon, ArrowRightIcon, CalendarDaysIcon, BookOpenIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Set document title for 404 pages
    document.title = '404 - Page Not Found | Connect & Co';
    
    // Track 404 for analytics (if available)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_not_found', {
        'page_location': window.location.href,
        'page_path': location.pathname,
        'event_category': '404_error',
        'custom_event_type': 'react_router_404'
      });
    }

    // Log for debugging
    console.log('📍 React Router 404:', location.pathname);
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-purple-50 py-20 pt-32 min-h-screen">
      <div className="max-w-2xl mx-auto px-6 text-center">
        
        {/* 404 Animation */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <div className="text-8xl md:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-4">
            404
          </div>
          <div className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            Page Not Found
          </div>
          <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
            The page you're looking for doesn't exist or has been moved. 
            Let's get you back to exploring our community!
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="space-y-4 mb-12"
        >
          <button
            onClick={() => handleNavigate('/')}
            className="inline-flex items-center bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 hover:scale-105 transition-all shadow-lg"
          >
            <HomeIcon className="w-5 h-5 mr-2" />
            Go to Homepage
          </button>
        </motion.div>

        {/* Popular Pages Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid md:grid-cols-2 gap-4 mb-8"
        >
          <button
            onClick={() => handleNavigate('/events')}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md hover:scale-105 transition-all text-left group"
          >
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <CalendarDaysIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-purple-600 transition-colors">
                  Upcoming Experiences
                </h3>
                <p className="text-sm text-gray-500">Swedish Fika, Forest Walks & More</p>
              </div>
            </div>
            <div className="flex items-center text-purple-600 text-sm font-medium">
              <span>Explore Experiences</span>
              <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => handleNavigate('/blog')}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md hover:scale-105 transition-all text-left group"
          >
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <BookOpenIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                  Community Stories
                </h3>
                <p className="text-sm text-gray-500">Travel, Culture & Connection</p>
              </div>
            </div>
            <div className="flex items-center text-blue-600 text-sm font-medium">
              <span>Read Stories</span>
              <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => handleNavigate('/conversation-starters')}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md hover:scale-105 transition-all text-left group"
          >
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-green-600 transition-colors">
                  Conversation Starters
                </h3>
                <p className="text-sm text-gray-500">Meaningful Connection Tips</p>
              </div>
            </div>
            <div className="flex items-center text-green-600 text-sm font-medium">
              <span>Get Started</span>
              <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => handleNavigate('/tokyo-survival-guide')}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md hover:scale-105 transition-all text-left group"
          >
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-amber-600 text-lg">🗾</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">
                  Tokyo Survival Guide
                </h3>
                <p className="text-sm text-gray-500">Free Expat Handbook</p>
              </div>
            </div>
            <div className="flex items-center text-amber-600 text-sm font-medium">
              <span>Download Free</span>
              <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </motion.div>

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
        >
          <p className="text-sm text-gray-500 mb-4">
            Looking for something specific?
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <button
              onClick={() => handleNavigate('/monthly-fika')}
              className="text-purple-600 hover:underline"
            >
              Monthly Fika
            </button>
            <span className="text-gray-300">•</span>
            <button
              onClick={() => handleNavigate('/goal-accountability-partners')}
              className="text-purple-600 hover:underline"
            >
              Goal Accountability
            </button>
            <span className="text-gray-300">•</span>
            <button
              onClick={() => {
                navigate('/contact');
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className="text-purple-600 hover:underline"
            >
              Contact Support
            </button>
          </div>
        </motion.div>

        {/* Debug Info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 bg-gray-100 rounded-lg text-left text-sm text-gray-600">
            <strong>Debug Info:</strong><br />
            Attempted URL: <code>{location.pathname}</code><br />
            Full URL: <code>{window.location.href}</code>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotFound;